// src/features/checkout/pages/CheckoutPage.tsx

import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  CircularProgress,
  Stack,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import editAddressIcon from "../../../assets/icons/icon-edit-address.svg";
import { useMutation } from "@tanstack/react-query";
import { addressService } from "../../myAccount/myAddress/services/addressService";
import { AddressModal } from "../../myAccount/myAddress/components/AddressModal";
import { checkoutService } from "../services/checkoutService";
// ❌ Card imports ปิดไว้ก่อน — PaySolutions จัดการ card เอง
// import { useQuery } from "@tanstack/react-query";
// import { paymentService, Card as ApiCard } from "../../setting/services/paymentService";
// import AddCardModal from "../../setting/components/AddCardModal";
import { Address } from "../../myAccount/myAddress/types";
import { CartItem } from "../../cart/types";
import { useCurrency } from "../../../contexts/CurrencyContext";
import { trackBeginCheckout } from "../../../services/analytics/analytics";

const parseBaseName = (name: string): string => {
  const dashIndex = name.lastIndexOf(" - ");
  return dashIndex === -1 ? name : name.slice(0, dashIndex).trim();
};

// ❌ CARD_TYPE_DISPLAY / CARD_COLORS ปิดไว้ก่อน — PaySolutions จัดการ card เอง

// ---- Choose Address Dialog ----
interface ChooseAddressDialogProps {
  open: boolean;
  onClose: () => void;
  addresses: Address[];
  selectedId?: string;
  onSelect: (address: Address) => void;
  onEdit: (address: Address) => void;
}

const ChooseAddressDialog: React.FC<ChooseAddressDialogProps> = ({
  open,
  onClose,
  addresses,
  selectedId,
  onSelect,
  onEdit,
}) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth={false}
    PaperProps={{ sx: { borderRadius: 2, width: { xs: '95%', sm: '50%' } } }}
  >
    <DialogTitle sx={{ fontWeight: 600, fontSize: "16px", pb: 1 }}>
      Choose Address
      <IconButton
        onClick={onClose}
        sx={{ position: "absolute", right: 12, top: 12, color: "#999" }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </DialogTitle>
    <DialogContent sx={{ pt: 1, pb: 3 }}>
      <Stack spacing={1.5}>
        {addresses.map((addr) => {
          const isSelected = selectedId === addr.id;
          return (
            <Card
              key={addr.id}
              onClick={() => {
                onSelect(addr);
                onClose();
              }}
              sx={{
                cursor: "pointer",
                border: isSelected ? "2px solid #1A1A1A" : "1px solid #E0E0E0",
                borderRadius: 2,
                boxShadow: "none",
                "&:hover": { borderColor: "#1A1A1A" },
              }}
            >
              <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 0.5,
                  }}
                >
                  <Typography sx={{ fontWeight: 600, fontSize: "14px" }}>
                    {addr.title || "Address"}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {addr.isDefault && (
                      <Typography sx={{ fontSize: "11px", color: "#888" }}>
                        Default Address
                      </Typography>
                    )}
                    {isSelected && (
                      <CheckIcon sx={{ fontSize: "16px", color: "#1A1A1A" }} />
                    )}
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); onEdit(addr); }}
                      sx={{ p: 0.5 }}
                    >
                      <img src={editAddressIcon} alt="Edit" width={18} height={18} />
                    </IconButton>
                  </Box>
                </Box>
                <Typography sx={{ fontSize: "13px", color: "#333" }}>
                  {addr.firstName} {addr.lastName}
                  {"  "}
                  <span style={{ color: "#666" }}>
                    {addr.phoneCode} {addr.phoneNumber}
                  </span>
                </Typography>
                <Typography sx={{ fontSize: "13px", color: "#666", mt: 0.25 }}>
                  {addr.address}, {addr.district}, {addr.province}{" "}
                  {addr.postcode}
                </Typography>
              </CardContent>
            </Card>
          );
        })}
      </Stack>
    </DialogContent>
  </Dialog>
);

// ---- Main Component ----
export const CheckoutPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const items: CartItem[] = (location.state as any)?.items ?? [];
  const { currency } = useCurrency();
  const currencySymbol = currency === 'THB' ? '฿' : '€';

  useEffect(() => {
    if (items.length === 0) {
      navigate("/cart", { replace: true });
      return;
    }
    trackBeginCheckout(
      currency,
      items.reduce((sum, i) => sum + (i.price?.[currency] ?? 0) * i.quantity, 0),
      items.map((i) => ({
        item_id: i.product_id,
        item_name: i.name,
        price: i.price?.[currency] ?? 0,
        quantity: i.quantity,
      })),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Address state ----
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<Address | undefined>();
  const [chooseAddressOpen, setChooseAddressOpen] = useState(false);
  const [addAddressOpen, setAddAddressOpen] = useState(false);
  const [isSavingAddress, setIsSavingAddress] = useState(false);
  const [editingAddress, setEditingAddress] = useState<Address | undefined>();
  const [editAddressOpen, setEditAddressOpen] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  // ---- Checkout ----
  const orderMutation = useMutation({
    mutationFn: checkoutService.placeOrder,
    onSuccess: (data) => {
      if (data.redirect_url) {
        window.location.href = data.redirect_url;
      }
    },
  });

  const handlePlaceOrder = () => {
    if (!selectedAddress) return;
    orderMutation.mutate({
      items: items.map((i) => ({ cart_key: i.cart_key })),
      address_id: selectedAddress.id!,
      currency,
    });
  };

  const loadAddresses = async () => {
    setLoadingAddresses(true);
    try {
      const result = await addressService.getAddresses();
      const addressList = Array.isArray(result.data) ? result.data : [];
      setAddresses(addressList);
      const defaultAddr = addressList.find((a) => a.isDefault);
      if (defaultAddr) {
        setSelectedAddress((prev) => prev ?? defaultAddr);
      }
    } catch {
      // no-op
    } finally {
      setLoadingAddresses(false);
    }
  };

  useEffect(() => {
    loadAddresses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaveAddress = async (addressData: Omit<Address, "id">) => {
    setIsSavingAddress(true);
    try {
      const response = await addressService.createAddress(addressData);
      if (response.success) {
        await loadAddresses();
        setAddAddressOpen(false);
      }
    } catch {
      // no-op
    } finally {
      setIsSavingAddress(false);
    }
  };

  const handleEditSave = async (addressData: Omit<Address, "id">) => {
    if (!editingAddress?.id) return;
    setIsSavingEdit(true);
    try {
      await addressService.updateAddress(editingAddress.id, addressData);
      await loadAddresses();
      setEditAddressOpen(false);
      setEditingAddress(undefined);
    } catch {
      // no-op
    } finally {
      setIsSavingEdit(false);
    }
  };

  const totalPrice = items.reduce((sum, i) => sum + (i.price?.[currency] ?? 0) * i.quantity, 0);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#fff" }}>
      <Container maxWidth={false} sx={{ pt: { xs: 8, md: 10 }, pb: 4, maxWidth: { xs: '100%', sm: '85%', md: '60%' } }}>
        {/* Title */}
        <Typography
          sx={{
            fontSize: "28px",
            fontWeight: 400,
            textAlign: "center",
            mb: 4,
            fontFamily: 'Nandia',
          }}
        >
          Shopping Bag
        </Typography>

        {/* ── Shipping Address ── */}
        <Box sx={{ mb: 2, display: "flex", flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'center' } }}>
          {loadingAddresses ? (
            <Box
              sx={{ display: "flex", justifyContent: "center", py: 2, flex: 1 }}
            >
              <CircularProgress size={20} sx={{ color: "#1A1A1A" }} />
            </Box>
          ) : (
            <>
              {/* Left: header + card — same width block */}
              <Box sx={{ flex: 1 }}>
                {/* Header row spans only the card width */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Typography
                    sx={{ fontSize: "13px", fontWeight: 500, color: "#333" }}
                  >
                    Shipping Address
                  </Typography>
                  <Button
                    size="small"
                    startIcon={
                      <LocationOnIcon sx={{ fontSize: "14px !important" }} />
                    }
                    onClick={() => setChooseAddressOpen(true)}
                    sx={{
                      fontSize: "12px",
                      color: "#555",
                      textTransform: "none",
                      p: 0,
                      minWidth: 0,
                      "&:hover": { bgcolor: "transparent", color: "#000" },
                    }}
                  >
                    Choose Address
                  </Button>
                </Box>

                {/* Address card */}
                <Box
                  sx={{
                    border: "1px solid #D0D0D0",
                    borderRadius: "4px",
                    overflow: "hidden",
                  }}
                >
                  {selectedAddress ? (
                    <>
                      {/* Header row with bottom border */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          px: 2,
                          py: 0.75,
                          borderBottom: "1px solid #E0E0E0",
                        }}
                      >
                        <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
                          {selectedAddress.title || "Address"}
                        </Typography>
                        {selectedAddress.isDefault && (
                          <Typography
                            sx={{
                              fontSize: "11px",
                              color: "#888",
                              fontStyle: "italic",
                            }}
                          >
                            Default Address
                          </Typography>
                        )}
                      </Box>
                      {/* Body */}
                      <Box
                        sx={{
                          px: 2,
                          py: 1.5,
                          minHeight: { xs: "auto", sm: "160px" },
                          alignContent: "center",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "16px",
                            fontWeight: 600,
                            color: "#333",
                            paddingBottom: "18px",
                          }}
                        >
                          {selectedAddress.firstName} {selectedAddress.lastName}
                          {"   "}
                          <span style={{ fontWeight: 400, color: "#555" }}>
                            {selectedAddress.phoneCode}{" "}
                            {selectedAddress.phoneNumber}
                          </span>
                        </Typography>
                        <Typography sx={{ fontSize: "16px", color: "#666" }}>
                          {selectedAddress.country || "Thailand"}
                        </Typography>
                        <Typography sx={{ fontSize: "16px", color: "#666" }}>
                          {selectedAddress.address}, {selectedAddress.district},{" "}
                          {selectedAddress.province} {selectedAddress.postcode}
                        </Typography>
                      </Box>
                    </>
                  ) : null}
                </Box>
              </Box>

              {/* Right: Add new address button */}
              <Box
                sx={{
                  width: { xs: '100%', sm: '43%' },
                }}
              >
                <Button
                  startIcon={<AddIcon sx={{ fontSize: "16px !important" }} />}
                  onClick={() => setAddAddressOpen(true)}
                  sx={{
                    fontSize: "13px",
                    color: "#aaa",
                    textTransform: "none",
                    border: "1px solid #D0D0D0",
                    borderRadius: "4px",
                    px: 3,
                    whiteSpace: "nowrap",
                    alignSelf: "center",
                    "&:hover": {
                      bgcolor: "#f5f5f5",
                      borderColor: "#999",
                      color: "#555",
                    },
                  }}
                >
                  Add new address
                </Button>
              </Box>
            </>
          )}
        </Box>

        {/* ── All Order ── */}
        <Box
          sx={{
            overflow: "hidden",
            mb: 2,
          }}
        >
          {/* Table header */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr 36px 48px 72px", sm: "1fr 60px 90px 90px" },
              px: { xs: 1, sm: 2 },
              py: 1,
              bgcolor: "#F7F7F7",
              border: "1px solid #D0D0D0",
              borderRadius: "4px",
            }}
          >
            <Typography sx={{ fontSize: { xs: "11px", sm: "13px" }, fontWeight: 500 }}>
              All Order
            </Typography>
            <Typography sx={{ fontSize: { xs: "11px", sm: "13px" }, fontWeight: 500, textAlign: "center" }}>
              Size
            </Typography>
            <Typography sx={{ fontSize: { xs: "11px", sm: "13px" }, fontWeight: 500, textAlign: "center" }}>
              Qty
            </Typography>
            <Typography sx={{ fontSize: { xs: "11px", sm: "13px" }, fontWeight: 500, textAlign: "center" }}>
              Price
            </Typography>
          </Box>

          {/* Rows */}
          {items.map((item) => {
            const baseName = parseBaseName(item.name);
            const size = (item.attributes?.size ?? '-').toUpperCase();
            return (
              <Box
                key={item.cart_key}
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr 36px 48px 72px", sm: "1fr 60px 90px 90px" },
                  alignItems: "center",
                  px: { xs: 1, sm: 2 },
                  py: { xs: 2, sm: 4 },
                  borderBottom: "1px solid #E6E6E6CC",
                }}
              >
                {/* Image + Name */}
                <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.5 } }}>
                  <Box
                    component="img"
                    src={item.image}
                    alt={baseName}
                    sx={{
                      width: { xs: 44, sm: 56 },
                      height: { xs: 44, sm: 56 },
                      objectFit: "cover",
                      flexShrink: 0,
                      borderRadius: "2px",
                    }}
                  />
                  <Typography sx={{ fontSize: { xs: "11px", sm: "13px" } }}>{baseName}</Typography>
                </Box>

                {/* Size */}
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <Box
                    sx={{
                      width: { xs: 24, sm: 28 },
                      height: { xs: 24, sm: 28 },
                      bgcolor: "#1A1A1A",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: { xs: "9px", sm: "11px" },
                      fontWeight: 600,
                      borderRadius: "2px",
                    }}
                  >
                    {size}
                  </Box>
                </Box>

                {/* Quantity */}
                <Typography sx={{ fontSize: { xs: "11px", sm: "13px" }, textAlign: "center" }}>
                  {item.quantity}
                </Typography>

                {/* Price */}
                <Typography sx={{ fontSize: { xs: "11px", sm: "13px" }, textAlign: "center" }}>
                  {currencySymbol} {((item.price?.[currency] ?? 0) * item.quantity).toLocaleString()}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* ── Payment Method ── */}
        
      </Container>

      {/* ── Sticky Footer Summary ── */}
      <Box
        sx={{
          borderTop: "1px solid #E0E0E0",
          px: 3,
          pt: 2,
          pb: 3,
          mt: '150px'
        }}
      >
        <Container maxWidth={false} disableGutters sx={{ maxWidth: { xs: '100%', md: '70%' } }}>
          <Box
            sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}
          >
            <Typography sx={{ fontSize: "13px", color: "#666" }}>
              Shipping Fee
            </Typography>
            <Typography sx={{ fontSize: "13px", color: "#666" }}>
              Free
            </Typography>
          </Box>
          <Box
            sx={{ display: "flex", justifyContent: "space-between", mb: 1.5 }}
          >
            <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
              Total {items.length} Items
            </Typography>
            <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
              {currencySymbol} {totalPrice.toLocaleString()}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, gap: 2 }}>
            <Typography
              sx={{ fontSize: "11px", color: "#888", lineHeight: 1.4, flex: 1 }}
            >
              By clicking "Place Order", I confirm that I have read and agree to{" "}
              <span style={{ textDecoration: "underline", cursor: "pointer" }}>
                Terms of Service
              </span>{" "}
              and{" "}
              <span style={{ textDecoration: "underline", cursor: "pointer" }}>
                Refund & Return Policy
              </span>
              .
            </Typography>

            <Button
              variant="contained"
              onClick={handlePlaceOrder}
              disabled={!selectedAddress || orderMutation.isPending}
              sx={{
                bgcolor: "#1A1A1A",
                color: "#fff",
                fontWeight: 500,
                fontSize: "14px",
                py: 1.25,
                px: 4,
                borderRadius: "4px",
                textTransform: "none",
                whiteSpace: "nowrap",
                width: { xs: '100%', sm: '309px' },
                flexShrink: 0,
                "&:hover": { bgcolor: "#333" },
                "&:disabled": { bgcolor: "#ccc", color: "#fff" },
              }}
            >
              {orderMutation.isPending ? (
                <CircularProgress size={20} sx={{ color: "#fff" }} />
              ) : (
                "Place Order"
              )}
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Choose Address Dialog */}
      <ChooseAddressDialog
        open={chooseAddressOpen}
        onClose={() => setChooseAddressOpen(false)}
        addresses={addresses}
        selectedId={selectedAddress?.id}
        onSelect={setSelectedAddress}
        onEdit={(addr) => {
          setEditingAddress(addr);
          setEditAddressOpen(true);
        }}
      />

      {/* Edit Address Modal */}
      <AddressModal
        open={editAddressOpen}
        onClose={() => { setEditAddressOpen(false); setEditingAddress(undefined); }}
        onSave={handleEditSave}
        initialData={editingAddress}
        isLoading={isSavingEdit}
      />

      {/* Add New Address Modal */}
      <AddressModal
        open={addAddressOpen}
        onClose={() => setAddAddressOpen(false)}
        onSave={handleSaveAddress}
        isLoading={isSavingAddress}
      />

      {/* ❌ AddCardModal ปิดไว้ก่อน — PaySolutions จัดการ card เอง */}
      {/* <AddCardModal key={addCardKey} open={addCardOpen} onClose={() => setAddCardOpen(false)} /> */}
    </Box>
  );
};

export default CheckoutPage;
